import { z } from "zod";

/**
 * Le contrat des recompenses de points de la chaine (lot QP, plan `docs/plans/2026-11-02-castellan-qp-recompenses.md`) :
 * ce que la page de la regie lit chez Twitch, ce qu'elle cree et regle, les etats de la recompense du Booster et les codes
 * de refus de ses routes. Twitch reste la seule verite : aucune table ici.
 */

/** Twitch n'accepte pas plus de 50 recompenses par chaine, desactivees comprises (`docs/veille/2026-10-12-castellan-twitch-api.md`, C13). */
export const RECOMPENSES_MAX = 50;
/** Le delai le plus court que l'interface de Twitch montre (1 minute), et le plus long qu'il accepte (7 jours). */
export const DELAI_MIN_SECONDES = 60;
export const DELAI_MAX_SECONDES = 604_800;

/**
 * Deux titres que Twitch tiendrait pour le meme. Sa doc dit « unique » sans parler de la casse : par prudence, ni la casse ni
 * les espaces de bord ne comptent.
 */
export const memeTitre = (a: string, b: string): boolean =>
  a.trim().toLocaleLowerCase("fr") === b.trim().toLocaleLowerCase("fr");

/** Un titre de recompense : montre aux viewers sur Twitch, donc ni caractere de commande ni invisible. */
export const titreRecompenseSchema = z
  .string()
  .trim()
  .min(1)
  .max(45)
  .refine((titre) => !/[\p{Cc}\p{Cf}\p{Zl}\p{Zp}]/u.test(titre), { message: "invisible" });

const reglageCommun = {
  titre: titreRecompenseSchema,
  prix: z.number().int().min(1).max(1_000_000),
  /** `null` : pas de limite par viewer et par stream. */
  limiteParStream: z.number().int().min(1).max(100).nullable(),
  /** `null` : pas de delai entre deux rachats. */
  delaiSecondes: z.number().int().min(DELAI_MIN_SECONDES).max(DELAI_MAX_SECONDES).nullable(),
};

/** `POST /api/twitch/recompenses` : la recompense nait desactivee, le corps n'a donc pas d'activation. */
export const creerRecompenseChaineSchema = z.strictObject(reglageCommun);
export type CreerRecompenseChaine = z.infer<typeof creerRecompenseChaineSchema>;

/** `PUT /api/twitch/recompenses/{id}` : le reglage complet d'une recompense de Castellan. */
export const reglerRecompenseChaineSchema = z.strictObject({
  ...reglageCommun,
  active: z.boolean(),
});
export type ReglerRecompenseChaine = z.infer<typeof reglerRecompenseChaineSchema>;

export const creationRecompenseSchema = z.object({ id: z.string() });

export const GERANTS_RECOMPENSE = ["castellan", "autre_app"] as const;
export type GerantRecompense = (typeof GERANTS_RECOMPENSE)[number];

/** Une recompense de la chaine, vue de la regie. */
export const recompenseChaineSchema = z.object({
  id: z.string(),
  titre: z.string(),
  prix: z.number().int(),
  active: z.boolean(),
  enPause: z.boolean(),
  limiteParStream: z.number().int().nullable(),
  delaiSecondes: z.number().int().nullable(),
  /** `castellan` : creee par son appli, il la regle. `autre_app` : il la voit, c'est tout. */
  gerant: z.enum(GERANTS_RECOMPENSE),
  /** Vrai pour le Booster du chateau, qui se regle dans sa page. */
  booster: z.boolean(),
});
export type RecompenseChaineVue = z.infer<typeof recompenseChaineSchema>;

/** Les etats de la recompense du Booster chez Twitch. `pleine` et `titre_pris` : Twitch a refuse, voir `rachats.ts`. */
export const ETATS_RECOMPENSE = [
  "a_creer",
  "a_pousser",
  "a_jour",
  "refusee",
  "pleine",
  "titre_pris",
] as const;
export type EtatRecompenseTwitch = (typeof ETATS_RECOMPENSE)[number];

/** `GET /api/twitch/recompenses`. Triees par titre. `booster` : ce que la page dit quand il n'est pas dans la liste. */
export const tableauRecompensesSchema = z.object({
  max: z.number().int(),
  total: z.number().int(),
  libres: z.number().int(),
  recompenses: z.array(recompenseChaineSchema),
  booster: z.object({
    titre: z.string(),
    etat: z.enum(ETATS_RECOMPENSE),
    /** Vrai quand il est dans la liste de la chaine. */
    present: z.boolean(),
    /**
     * Vrai quand cette lecture a demande une nouvelle synchronisation du Booster, sans attendre : la cause de son blocage
     * a disparu (une place libre, un titre plus pris), ou il a ete supprime a la main. La demande ne fait rien en pause ni
     * sans travail de fond, et peut echouer : ni la reponse ni la page ne promettent qu'elle aboutisse. `etat` est celui
     * d'avant la demande : la page ne le donne pas comme definitif, et se relit.
     */
    relance: z.boolean(),
  }),
});
export type TableauRecompenses = z.infer<typeof tableauRecompensesSchema>;

/** Les codes qu'une route des recompenses peut rendre, en plus de ceux de la garde. Chacun a sa phrase dans la page. */
export const CODES_RECOMPENSES = [
  "twitch_non_relie",
  "twitch_refuse",
  "chaine_non_affiliee",
  "recompenses_pleines",
  "titre_pris",
  "recompense_introuvable",
  "recompense_autre_app",
  "recompense_booster",
  "base_indisponible",
] as const;
export type CodeRecompenses = (typeof CODES_RECOMPENSES)[number];
